import { createClient } from '@supabase/supabase-js';

const SUPABASE_URL  = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY  = process.env.REACT_APP_SUPABASE_ANON_KEY;

const supabaseReady = !!(SUPABASE_URL && SUPABASE_KEY);
export const supabase = supabaseReady ? createClient(SUPABASE_URL, SUPABASE_KEY) : null;

export const STATE_ROW_ID = 'sdassp_fo_v1';

// Tracks when WE last saved, to avoid reapplying our own Realtime event
let lastSaveTime = 0;

export async function loadRemoteState() {
  if (!supabaseReady) return null;
  try {
    const { data, error } = await supabase
      .from('app_state')
      .select('state')
      .eq('id', STATE_ROW_ID)
      .single();
    if (error || !data) return null;
    return data.state;
  } catch { return null; }
}

export async function loadRemoteStateFull() {
  if (!supabaseReady) return null;
  try {
    const { data, error } = await supabase
      .from('app_state')
      .select('state, updated_at')
      .eq('id', STATE_ROW_ID)
      .single();
    if (error || !data) return null;
    return { state: data.state, updatedAt: data.updated_at };
  } catch { return null; }
}

// Strip base64 data before saving to Supabase — conserver les URLs Storage
function stripDocuments(state) {
  if (!state) return state;
  const noData = (docs) => (docs || []).map(({ data: _d, ...rest }) => rest); // eslint-disable-line
  const stripDoc = (d) => {
    if (!d) return d;
    if (typeof d === 'string') return d.startsWith('data:') ? '[base64]' : d; // URL Storage conservée
    return { ...d, data: undefined }; // objet: garder url, supprimer data
  };
  return {
    ...state,
    _deletedIds: state._deletedIds || [], // explicite : jamais strippé
    adherents: (state.adherents || []).map(a => ({ ...a, documents: noData(a.documents) })),
    demandes: (state.demandes || []).map(d => ({
      ...d,
      document:           stripDoc(d.document),
      complementDocument: d.complementDocument ? { ...d.complementDocument, data: undefined } : undefined,
      sepaDocument:       d.sepaDocument       ? { ...d.sepaDocument,       data: undefined } : undefined,
    })),
    // Supprime le base64 des justificatifs de dépenses — seules les métadonnées Storage sont conservées
    depenses: (state.depenses || []).map(dep => {
      if (!dep.justificatif?.data) return dep;
      const { data: _d, ...jMeta } = dep.justificatif; // eslint-disable-line no-unused-vars
      return { ...dep, justificatif: jMeta };
    }),
  };
}

// ── Supabase Storage (pour les fichiers/documents) ──

export async function uploadToStorage(path, base64Data, mimeType) {
  if (!supabaseReady || !base64Data) return null;
  try {
    const b64 = base64Data.split(',')[1];
    if (!b64) return null;
    const bytes = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    const { error } = await supabase.storage
      .from('documents')
      .upload(path, bytes, { contentType: mimeType || 'application/octet-stream', upsert: true });
    if (error) { console.warn('Storage upload:', error.message); return null; }
    return path; // Retourne le storagePath — l'URL (publique ou signée) est générée séparément
  } catch (e) { console.warn('Storage error:', e); return null; }
}

export async function deleteFromStorage(paths) {
  if (!supabaseReady || !paths?.length) return;
  try { await supabase.storage.from('documents').remove(paths); } catch {}
}

// Génère l'URL publique d'un fichier (bucket public uniquement)
// ⚠️  À remplacer par createSignedUrl si le bucket `documents` est passé en privé
export function getStoragePublicUrl(storagePath) {
  if (!supabaseReady || !storagePath) return null;
  const { data } = supabase.storage.from('documents').getPublicUrl(storagePath);
  return data?.publicUrl || null;
}

// Génère une signed URL temporaire pour un fichier privé (défaut : 1 heure)
export async function createSignedUrl(storagePath, expiresInSeconds = 3600) {
  if (!supabaseReady || !storagePath) return null;
  try {
    const { data, error } = await supabase.storage
      .from('documents')
      .createSignedUrl(storagePath, expiresInSeconds);
    if (error) { console.warn('[Storage] createSignedUrl error:', error.message); return null; }
    return data?.signedUrl || null;
  } catch (e) { console.warn('[Storage] createSignedUrl exception:', e); return null; }
}

export async function saveRemoteState(state) {
  if (!supabaseReady) return false;
  try {
    const stripped = stripDocuments(state);
    const { error } = await supabase
      .from('app_state')
      .upsert({ id: STATE_ROW_ID, state: stripped, updated_at: new Date().toISOString() });
    if (error) { console.error('[Supabase] saveRemoteState error:', error.message); return false; }
    lastSaveTime = Date.now();
    return true;
  } catch (e) { console.error('[Supabase] saveRemoteState exception:', e); return false; }
}

/**
 * Subscribe to remote state changes from OTHER users.
 * callback(newState) is called when a colleague saves new data.
 * Returns an unsubscribe function.
 */
// Reset codes stored in a SEPARATE row to avoid race conditions with the main state
const RESET_ROW_ID = 'sdassp_fo_reset_codes';

export async function saveResetCode(email, code, userId) {
  if (!supabaseReady) return;
  try {
    const entry = { code, userId, expiry: new Date(Date.now() + 15 * 60 * 1000).toISOString() };
    // Read existing reset codes row (separate from main state)
    const { data } = await supabase.from('app_state').select('state').eq('id', RESET_ROW_ID).single();
    const codes = { ...(data?.state || {}), [email]: entry };
    await supabase.from('app_state').upsert({ id: RESET_ROW_ID, state: codes, updated_at: new Date().toISOString() });
  } catch (e) { console.error('[Supabase] saveResetCode:', e); }
}

export async function verifyResetCode(email, code) {
  if (!supabaseReady) return null;
  try {
    const { data } = await supabase.from('app_state').select('state').eq('id', RESET_ROW_ID).single();
    const entry = data?.state?.[email];
    if (!entry) return null;
    if (entry.code !== code) return null;
    if (new Date(entry.expiry) < new Date()) return null;
    return entry.userId;
  } catch { return null; }
}

export async function clearResetCode(email) {
  if (!supabaseReady) return;
  try {
    const { data } = await supabase.from('app_state').select('state').eq('id', RESET_ROW_ID).single();
    const codes = { ...(data?.state || {}) };
    delete codes[email];
    await supabase.from('app_state').upsert({ id: RESET_ROW_ID, state: codes, updated_at: new Date().toISOString() });
  } catch (e) { console.error('[Supabase] clearResetCode:', e); }
}

// ── Anciens stubs (neutralisés — plus d'écriture base64 en BDD) ──
export async function saveDocumentCloud() {}
export async function loadDocumentsCloud() { return {}; }
export async function deleteDocumentsCloud() {}
export async function saveCloudBlob() {}
export async function loadCloudBlobs() { return {}; }
export async function deleteCloudBlobs() {}

// ══════════════════════════════════════════════════════════════════════
// ── Architecture par entité (remplace le blob JSON unique) ──
// ══════════════════════════════════════════════════════════════════════

// Correspondance clé React state → table Supabase
const ENTITY_MAP = {
  adherents:     'adherents',
  demandes:      'demandes',
  etablissements:'etablissements',
  users:         'app_users',
  connexions:    'connexions',
  convocations:  'convocations',
  conseilMembers:'conseil_members',
  depenses:      'depenses',
};

// Clés de config globale (pas des tableaux d'entités)
const CONFIG_KEYS = [
  'selectedYear','pins','_deletedIds','cotisations',
  'budgetDepenses','compteResultat','solde',
];

// Strip base64 par entité
function stripEntityDocs(entity, entityKey) {
  if (!entity) return entity;
  if (entityKey === 'adherents') {
    return { ...entity, documents: (entity.documents || []).map(({ data: _d, ...rest }) => rest) }; // eslint-disable-line
  }
  if (entityKey === 'demandes') {
    const stripDoc = (d) => {
      if (!d) return d;
      if (typeof d === 'string') return d.startsWith('data:') ? '[base64]' : d;
      return { ...d, data: undefined };
    };
    return {
      ...entity,
      document:           stripDoc(entity.document),
      complementDocument: entity.complementDocument ? { ...entity.complementDocument, data: undefined } : undefined,
      sepaDocument:       entity.sepaDocument       ? { ...entity.sepaDocument,       data: undefined } : undefined,
    };
  }
  if (entityKey === 'depenses') {
    if (!entity.justificatif?.data) return entity;
    const { data: _d, ...jMeta } = entity.justificatif; // eslint-disable-line no-unused-vars
    return { ...entity, justificatif: jMeta };
  }
  return entity;
}

// Détecte si deux entités ont la même version (pour les deltas)
function entityVersion(e, entityKey) {
  if (!e) return '';
  return e.updatedAt || e.dateCreation || e.dateAdhesion || e.ts || e.id || '';
}

/**
 * Sauvegarde par entité : upsert uniquement les entités modifiées/ajoutées
 * et supprime les entités retirées. Retourne true si tout OK.
 */
export async function saveEntitiesToTables(state, prevState = null) {
  if (!supabaseReady) return false;
  const now = new Date().toISOString();
  const promises = [];
  let tablesReady = true;

  for (const [stateKey, tableName] of Object.entries(ENTITY_MAP)) {
    const curr = state[stateKey] || [];
    const prev = prevState ? (prevState[stateKey] || []) : [];
    const prevMap = new Map(prev.map(e => [e.id, e]));
    const currIds = new Set(curr.map(e => e.id));

    // Entités nouvelles ou modifiées
    const toUpsert = curr.filter(e => {
      const p = prevMap.get(e.id);
      return !p || entityVersion(e, stateKey) !== entityVersion(p, stateKey);
    });

    // Entités supprimées
    const toDelete = prev.filter(e => !currIds.has(e.id)).map(e => e.id);

    if (toUpsert.length > 0) {
      const tsCol = (tableName === 'connexions' || tableName === 'journal_entries') ? 'ts' : 'updated_at';
      const rows = toUpsert.map(e => ({
        id: e.id,
        data: stripEntityDocs(e, stateKey),
        [tsCol]: e.updatedAt || e.dateCreation || e.ts || now,
      }));
      promises.push(
        supabase.from(tableName).upsert(rows, { onConflict: 'id' }).then(r => {
          if (r.error?.code === '42P01') { tablesReady = false; } // table doesn't exist
          return r;
        })
      );
    }

    if (toDelete.length > 0) {
      promises.push(supabase.from(tableName).delete().in('id', toDelete));
    }
  }

  // Config globale
  const config = {};
  CONFIG_KEYS.forEach(k => { if (state[k] !== undefined) config[k] = state[k]; });
  const prevConfig = {};
  if (prevState) CONFIG_KEYS.forEach(k => { if (prevState[k] !== undefined) prevConfig[k] = prevState[k]; });
  if (JSON.stringify(config) !== JSON.stringify(prevConfig)) {
    promises.push(supabase.from('app_config').upsert({ key: 'main', value: config, updated_at: now }, { onConflict: 'key' }));
  }

  if (promises.length === 0) return true; // nothing to save

  const results = await Promise.allSettled(promises);
  if (!tablesReady) return false; // tables not created yet → fallback

  const failed = results.filter(r => r.status === 'rejected' || r.value?.error);
  if (failed.length) { console.error('[Supabase] saveEntitiesToTables errors:', failed.map(f => f.reason || f.value?.error)); }
  return failed.length === 0;
}

/**
 * Charge toutes les entités depuis les tables Supabase.
 * Retourne null si les tables n'existent pas encore (fallback vers blob).
 */
export async function loadEntitiesFromTables() {
  if (!supabaseReady) return null;
  try {
    const [
      adherentsR, demandesR, etablissementsR, usersR,
      connexionsR, convocationsR, conseilR, depensesR, configR,
    ] = await Promise.all([
      supabase.from('adherents').select('data').order('updated_at', { ascending: false }),
      supabase.from('demandes').select('data').order('updated_at', { ascending: false }),
      supabase.from('etablissements').select('data').order('updated_at', { ascending: false }),
      supabase.from('app_users').select('data').order('updated_at', { ascending: false }),
      supabase.from('connexions').select('data').order('ts', { ascending: false }).limit(100),
      supabase.from('convocations').select('data').order('updated_at', { ascending: false }),
      supabase.from('conseil_members').select('data').order('updated_at', { ascending: false }),
      supabase.from('depenses').select('data').order('updated_at', { ascending: false }),
      supabase.from('app_config').select('value').eq('key', 'main').maybeSingle(),
    ]);

    // Si une table n'existe pas encore, fallback au blob
    if (adherentsR.error?.code === '42P01' || adherentsR.error?.message?.includes('does not exist')) return null;
    // Tables vides = pas encore migré = fallback au blob
    if ((adherentsR.data || []).length === 0 && (usersR.data || []).length === 0) return null;

    const config = configR.data?.value || {};

    return {
      adherents:      (adherentsR.data      || []).map(r => r.data),
      demandes:       (demandesR.data       || []).map(r => r.data),
      etablissements: (etablissementsR.data || []).map(r => r.data),
      users:          (usersR.data          || []).map(r => r.data),
      connexions:     (connexionsR.data     || []).map(r => r.data),
      convocations:   (convocationsR.data   || []).map(r => r.data),
      conseilMembers: (conseilR.data        || []).map(r => r.data),
      depenses:       (depensesR.data       || []).map(r => r.data),
      ...config,
    };
  } catch (e) {
    console.error('[Supabase] loadEntitiesFromTables exception:', e);
    return null;
  }
}

/**
 * Subscribe aux changements par entité depuis les tables Supabase.
 * callback({ entityKey, eventType, entity, deletedId })
 */
export function subscribeToEntities(callback) {
  if (!supabaseReady || !supabase) return () => {};

  const TABLE_TO_KEY = Object.fromEntries(Object.entries(ENTITY_MAP).map(([k, v]) => [v, k]));

  const handlePayload = (payload) => {
    if (Date.now() - lastSaveTime < 3000) return; // our own save
    const { eventType, new: newRow, old: oldRow, table } = payload;
    const entityKey = TABLE_TO_KEY[table];
    if (!entityKey) return;

    callback({
      entityKey,
      eventType,
      entity:    eventType !== 'DELETE' ? newRow?.data : null,
      deletedId: eventType === 'DELETE'  ? oldRow?.id  : null,
    });
  };

  const channel = supabase.channel('entities_realtime_v2');
  Object.values(ENTITY_MAP).forEach(tableName => {
    channel.on('postgres_changes', { event: '*', schema: 'public', table: tableName }, handlePayload);
  });
  channel.subscribe();

  return () => { try { supabase.removeChannel(channel); } catch {} };
}

export function subscribeToState(callback) {
  if (!supabaseReady || !supabase) return () => {};
  const channel = supabase
    .channel('app_state_realtime')
    .on(
      'postgres_changes',
      { event: 'UPDATE', schema: 'public', table: 'app_state', filter: `id=eq.${STATE_ROW_ID}` },
      (payload) => {
        // Ignore events triggered by our own recent save (within 3 seconds)
        if (Date.now() - lastSaveTime < 3000) return;
        const remoteState = payload.new?.state;
        if (remoteState) callback(remoteState);
      }
    )
    .subscribe();
  return () => { try { supabase.removeChannel(channel); } catch {} };
}
